import ExcelJS from 'exceljs'
import { saveAs } from 'file-saver'
import _ from 'lodash'
import moment from 'moment'
import { getCrm, numberFormat, toBuddhistYear } from 'utils/app.utils'
import { forceTextOnDateCells } from 'utils/excel.utils'

async function ExportToExcel({
    data,
    doctors,
    branches,
    branchId,
    filter,
    filterDateStart,
    filterDateEnd,
    snType,
    t,
}: {
    data: any
    doctors: any[]
    branches: any[]
    branchId: number
    filter: any[]
    filterDateStart: string
    filterDateEnd: string
    snType: boolean
    t: any
}) {
    const hasCrm = getCrm()
    const workbook = new ExcelJS.Workbook()
    const sheet = workbook.addWorksheet(t('REPORT.REPORT_NAME.REMUNERATION'))

    const titleRow = sheet.addRow([t('REPORT.REPORT_NAME.REMUNERATION')])
    titleRow.font = { bold: true, size: 16 }
    sheet.mergeCells(titleRow.number, 1, titleRow.number, 20)
    titleRow.alignment = { horizontal: 'left' }

    const filterLabels = [
        t('REPORT.FILTER.BRANCH'),
        t('REPORT.FILTER.DATE_START'),
        t('REPORT.FILTER.DATE_END'),
        t('REPORT.FILTER.RECEIPT_TYPE'),
        t('REPORT.FILTER.DOCTOR'),
    ]
    const filterValues = [
        branchId === 0 ? t('REPORT.FILTER.ALL') : _.get(_.find(branches, { branchId }), 'branchName', ''),
        filterDateStart ? toBuddhistYear(moment(filterDateStart), 'DD/MM/YYYY') : '-',
        filterDateEnd ? toBuddhistYear(moment(filterDateEnd), 'DD/MM/YYYY') : '-',
        filter[3] || t('REPORT.FILTER.ALL'),
        filter[4] || t('REPORT.FILTER.ALL'),
    ]

    const filterRowData: any[] = []
    for (let i = 0; i < filterLabels.length; i++) {
        filterRowData.push(filterLabels[i] + ':', filterValues[i])
    }
    const filterRow = sheet.addRow(filterRowData)
    filterRow.font = { size: 11 }
    for (let col = 1; col <= filterRowData.length; col += 2) {
        filterRow.getCell(col).font = { bold: true }
        filterRow.getCell(col).alignment = { horizontal: 'left' }
        filterRow.getCell(col + 1).alignment = { horizontal: 'left' }
    }

    const headCells = [
        { label: t('REPORT.TABLE.REMUNERATION.#NO'), width: 10 },
        { label: t('REPORT.TABLE.REMUNERATION.BILL_ID'), width: 15 },
        { label: t('REPORT.TABLE.REMUNERATION.CN'), width: 15 },
        { label: t('REPORT.TABLE.REMUNERATION.BRANCH_CN'), width: 15 },
        { label: t('REPORT.TABLE.REMUNERATION.PATIENT_SN_CODE'), width: 15 },
        { label: t('REPORT.TABLE.REMUNERATION.PATIENT_NAME'), width: 25 },
        { label: t('REPORT.TABLE.REMUNERATION.TX'), width: 10 },
        { label: t('REPORT.TABLE.REMUNERATION.MED'), width: 10 },
        { label: t('REPORT.TABLE.REMUNERATION.PD'), width: 10 },
        { label: t('REPORT.TABLE.REMUNERATION.MAT'), width: 10 },
        { label: t('REPORT.TABLE.REMUNERATION.PAYMENT'), width: 12 },
        { label: t('REPORT.TABLE.REMUNERATION.CASH'), width: 12 },
        { label: t('REPORT.TABLE.REMUNERATION.TRANSFER'), width: 12 },
        { label: t('REPORT.TABLE.REMUNERATION.DEBIT'), width: 12 },
        { label: t('REPORT.TABLE.REMUNERATION.CREDIT'), width: 12 },
        { label: t('REPORT.TABLE.REMUNERATION.TREATMENT_RIGHT'), width: 12 },
        ...(hasCrm ? [
            { label: t('REPORT.TABLE.REMUNERATION.VOUCHER'), width: 12 },
            { label: t('REPORT.TABLE.REMUNERATION.PROMOTION'), width: 12 },
        ] : []),
        { label: t('REPORT.TABLE.REMUNERATION.ADVANCE'), width: 12 },
        { label: t('REPORT.TABLE.REMUNERATION.DF'), width: 12 },
        { label: t('REPORT.TABLE.REMUNERATION.CREDIT_DF'), width: 12 },
        { label: t('REPORT.TABLE.REMUNERATION.TREATMENT_RIGHT_DF'), width: 12 },
        { label: t('REPORT.TABLE.REMUNERATION.NET_DF'), width: 12 },
    ]
    if (!snType) {
        headCells.splice(4, 1)
    }

    sheet.columns = headCells.map(c => ({ width: c.width }))

    const headerRow = sheet.addRow(headCells.map(c => c.label))
    headerRow.font = { bold: true, size: 12 }
    headerRow.alignment = { horizontal: 'left', vertical: 'middle' }

    if (!data.rows || data.rows.length === 0) {
        const noDataRow = sheet.addRow([t('REPORT.TITLE.NO_DATA')])
        sheet.mergeCells(noDataRow.number, 1, noDataRow.number, headCells.length)
        noDataRow.font = { italic: true, color: { argb: 'FF000000' } }
        noDataRow.alignment = { horizontal: 'center' }
    } else {
        for (const salary of data.rows) {
            const dentist = _.get(_.find(doctors, { dentistId: Number(salary.doctorId) }), 'dentistFullname', salary.doctor || '-')
            const dentistRow = sheet.addRow([`ทันตแพทย์: ${dentist}`])
            dentistRow.font = { bold: true }
            sheet.mergeCells(dentistRow.number, 1, dentistRow.number, headCells.length)

            const grouped = _.groupBy(salary.table, (item) => toBuddhistYear(moment(item.date), 'DD/MM/YYYY'))
            for (const [dateStr, items] of Object.entries(grouped)) {
                const dateRow = sheet.addRow([`วันที่: ${dateStr}`])
                dateRow.font = { italic: true }
                sheet.mergeCells(dateRow.number, 1, dateRow.number, headCells.length)

                let rowIndex = 1
                for (let i = 0; i < items.length; i++) {
                    const d = items[i]
                    const row = [
                        rowIndex++,
                        (d.billNo || '-') + (d?.isNhso === '1' ? ` (สปสช.) ` : ''),
                        d.cnNumber || '-',
                        d.branchCnNumber || '-',
                        d.snCode || '-',
                        d.patientFullname || '-',
                        numberFormat(d.paidTx),
                        numberFormat(d.paidMed),
                        numberFormat(d.paidPd),
                        numberFormat(d.paidMat),
                        numberFormat(d.amountPaid),
                        numberFormat(d.amountCash),
                        numberFormat(d.amountTransfer),
                        numberFormat(d.amountDebit),
                        numberFormat(d.amountCredit),
                        numberFormat(d.amountTreatmentRight),
                        ...(hasCrm ? [
                            numberFormat(d.amountVoucher),
                            numberFormat(d.amountPromotion),
                        ] : []),
                        numberFormat(d.amountAdvance),
                        numberFormat(d.dfPaid),
                        numberFormat(d.creditCost),
                        numberFormat(d.dfLostTreatment),
                        numberFormat(d.dfNet),
                    ]
                    if (!snType) {
                        row.splice(4, 1)
                    }
                    sheet.addRow(row)
                }

                // รวมค่าแต่ละ field ของ items
                const sumPaidTx = _.sumBy(items, 'paidTx')
                const sumPaidMed = _.sumBy(items, 'paidMed')
                const sumPaidPd = _.sumBy(items, 'paidPd')
                const sumPaidMat = _.sumBy(items, 'paidMat')
                const sumAmountPaid = _.sumBy(items, 'amountPaid')
                const sumCash = _.sumBy(items, 'amountCash')
                const sumTransfer = _.sumBy(items, 'amountTransfer')
                const sumDebit = _.sumBy(items, 'amountDebit')
                const sumCredit = _.sumBy(items, 'amountCredit')
                const sumTreatmentRight = _.sumBy(items, 'amountTreatmentRight')
                const sumVoucher = _.sumBy(items, 'amountVoucher')
                const sumPromotion = _.sumBy(items, 'amountPromotion')
                const sumAdvance = _.sumBy(items, 'amountAdvance')
                const sumDfPaid = _.sumBy(items, 'dfPaid')
                const sumCreditCost = _.sumBy(items, 'creditCost')
                const sumDfLostTreatment = _.sumBy(items, 'dfLostTreatment')
                const sumDfNet = _.sumBy(items, 'dfNet')

                const totalRowData = [
                    'รวม',
                    '', '', '', '',
                    numberFormat(sumPaidTx),
                    numberFormat(sumPaidMed),
                    numberFormat(sumPaidPd),
                    numberFormat(sumPaidMat),
                    numberFormat(sumAmountPaid),
                    numberFormat(sumCash),
                    numberFormat(sumTransfer),
                    numberFormat(sumDebit),
                    numberFormat(sumCredit),
                    numberFormat(sumTreatmentRight),
                    ...(hasCrm ? [
                        numberFormat(sumVoucher),
                        numberFormat(sumPromotion),
                    ] : []),
                    numberFormat(sumAdvance),
                    numberFormat(sumDfPaid),
                    numberFormat(sumCreditCost),
                    numberFormat(sumDfLostTreatment),
                    numberFormat(sumDfNet),
                ]
                const totalRow = sheet.addRow(totalRowData)
                sheet.mergeCells(totalRow.number, 1, totalRow.number, 5)
                totalRow.font = { bold: true }
                totalRow.alignment = { horizontal: 'right' }
                sheet.addRow([])
            }
        }
    }


    if (data.rowLab && data.rowLab.length) {
        sheet.addRow([])

        const labHdr = [
            t('REPORT.TABLE.REMUNERATION.#NO'),
            t('REPORT.TABLE.REMUNERATION.DOCTOR'),
            t('REPORT.TABLE.REMUNERATION.CN'),
            t('REPORT.TABLE.REMUNERATION.BRANCH_CN'),
            t('REPORT.TABLE.REMUNERATION.PATIENT_SN_CODE'),
            t('REPORT.TABLE.REMUNERATION.PATIENT_NAME'),
            t('REPORT.TABLE.REMUNERATION.DESCRIPTION'),
            t('REPORT.TABLE.REMUNERATION.LAB_NAME'),
            t('REPORT.TABLE.REMUNERATION.JOB'),
            t('LAB Fee'),
            t('DF LAB Fee'),
        ]
        if (!snType) {
            labHdr.splice(4, 1)
        }
        const labHeaderRow = sheet.addRow(labHdr)
        labHeaderRow.font = { bold: true, size: 11 }
        labHeaderRow.alignment = { horizontal: 'left', vertical: 'middle' }
        const labColWidths = [15, 20, 20, 20, 20, 25, 30, 25, 25, 15, 15]

        labColWidths.forEach((width, idx) => {
            sheet.getColumn(idx + 1).width = width
        })

        data.rowLab.forEach((labGroup: any) => {
            const labDateRow = sheet.addRow([`วันที่: ${toBuddhistYear(moment(labGroup.date), 'DD/MM/YYYY')}`])
            sheet.mergeCells(labDateRow.number, 1, labDateRow.number, labHdr.length)

            labGroup.table.forEach((labItem: any, idx: number) => {
                const row = [
                    idx + 1,
                    labItem.dentist || '-',
                    labItem.cnNumber || '-',
                    labItem.branchCnNumber || '-',
                    labItem.snCode || '-',
                    labItem.patientFullname || '-',
                    labItem.remark || '-',
                    labItem.lab || '-',
                    labItem.operativeName || '-',
                    numberFormat(labItem.labFee),
                    numberFormat(labItem.dfLabFee),
                ]
                if (!snType) {
                    row.splice(4, 1)
                }
                sheet.addRow(row)
            })

            const sumLabFee = _.sumBy(labGroup.table, 'labFee')
            const sumDfLabFee = _.sumBy(labGroup.table, 'dfLabFee')

            const totalLabRowData = [
                'รวม',
                '', '', '', '', '', '', '', '',
                numberFormat(sumLabFee),
                numberFormat(sumDfLabFee),
            ]
            if (!snType) {
                totalLabRowData.splice(4, 1)
            }

            const totalLabRow = sheet.addRow(totalLabRowData)
            sheet.mergeCells(totalLabRow.number, 1, totalLabRow.number, totalLabRowData.length - 2) // 8 หรือ 7 ควบคุม span ไม่ให้เกินคอลัมน์
            totalLabRow.font = { bold: true }
            totalLabRow.alignment = { horizontal: 'right' }
            sheet.addRow([])
        })
    } else {
        sheet.addRow([])
        const labHdr = [
            t('REPORT.TABLE.REMUNERATION.#NO'),
            t('REPORT.TABLE.REMUNERATION.DOCTOR'),
            t('REPORT.TABLE.REMUNERATION.CN'),
            t('REPORT.TABLE.REMUNERATION.BRANCH_CN'),
            t('REPORT.TABLE.REMUNERATION.PATIENT_SN_CODE'),
            t('REPORT.TABLE.REMUNERATION.PATIENT_NAME'),
            t('REPORT.TABLE.REMUNERATION.DESCRIPTION'),
            t('REPORT.TABLE.REMUNERATION.LAB_NAME'),
            t('REPORT.TABLE.REMUNERATION.JOB'),
            t('LAB Fee'),
            t('DF LAB Fee'),
        ]
        if (!snType) {
            labHdr.splice(4, 1)
        }
        const labHeaderRow = sheet.addRow(labHdr)
        labHeaderRow.font = { bold: true, size: 11 }
        labHeaderRow.alignment = { horizontal: 'left', vertical: 'middle' }
        const labColWidths = [15, 20, 20, 20, 20, 25, 30, 25, 25, 15, 15]

        labColWidths.forEach((width, idx) => {
            sheet.getColumn(idx + 1).width = width
        })
        const noDataRow = sheet.addRow([t('REPORT.TITLE.NO_DATA')])
        sheet.mergeCells(noDataRow.number, 1, noDataRow.number, 11)
        noDataRow.font = { italic: true, color: { argb: 'FF000000' } }
        noDataRow.alignment = { horizontal: 'center' }
    }


    if (data.rowDf && data.rowDf.length) {
        sheet.addRow([])
        const dfHeader = [
            t('REPORT.TABLE.REMUNERATION.#NO'),
            t('REPORT.TABLE.REMUNERATION.DOCTOR'),
            t('REPORT.TABLE.REMUNERATION.NET_PRICE'),
            t('ค่าประกันมือ'),
            t('REPORT.TABLE.REMUNERATION.SUM_PAID'),
            t('DF'),
            t('REPORT.TABLE.REMUNERATION.CREDIT_DF'),
            t('REPORT.TABLE.REMUNERATION.TREATMENT_RIGHT_DF'),
            t('REPORT.TABLE.REMUNERATION.SUM_DF'),
            t('DF LAB'),
            t('Net DF'),
        ]
        sheet.addRow(dfHeader).font = { bold: true }
        data.rowDf.forEach((df: any, idx: number) => {
            const totalDFLab = (Number(df.dfLostCreditAndTreatment) || 0) - (Number(df.dfNet) || 0)
            const newDFLab = totalDFLab < 0 ? 0 : totalDFLab
            const row = [
                idx + 1,
                _.get(_.find(doctors, { dentistId: Number(df.doctorId) }), 'dentistFullname') || '-',
                numberFormat(df.totalAmount),
                numberFormat(df.handInsurance),
                numberFormat(df.amountPaid),
                numberFormat(df.amountDf),
                numberFormat(df.dfCredit),
                numberFormat(df.dfLostTreatment),
                numberFormat(df.dfLostCreditAndTreatment),
                numberFormat(newDFLab),
                numberFormat(df.dfNet),
            ]
            sheet.addRow(row)
        })

        const totaSumlDFLab = data?.rowDf.length ? data.rowDf.map((item: any) => {
            const newSum = (item?.dfLostCreditAndTreatment || 0) - (parseFloat((item?.dfNet || 0).toFixed(2)))
            if (newSum < 0) {
                return 0
            } else {
                return newSum
            }
        }).reduce((a: any, b: any) => Number(a || 0) + Number(b || 0)) : 0
        const newSumDFLab = totaSumlDFLab < 0 ? 0 : totaSumlDFLab

        const sumTotalAmount = _.sumBy(data.rowDf, 'totalAmount')
        const sumHandInsurance = _.sumBy(data.rowDf, 'handInsurance')
        const sumAmountPaid = _.sumBy(data.rowDf, 'amountPaid')
        const sumAmountDf = _.sumBy(data.rowDf, 'amountDf')
        const sumDfCredit = _.sumBy(data.rowDf, 'dfCredit')
        const sumDfLostTreatment = _.sumBy(data.rowDf, 'dfLostTreatment')
        const sumDfLostCreditAndTreatment = _.sumBy(data.rowDf, 'dfLostCreditAndTreatment')
        const sumDfLabFee = newSumDFLab
        const sumDfNet = _.sumBy(data.rowDf, 'dfNet')

        const totalDfRow = sheet.addRow([
            'รวม',
            '',
            numberFormat(sumTotalAmount),
            numberFormat(sumHandInsurance),
            numberFormat(sumAmountPaid),
            numberFormat(sumAmountDf),
            numberFormat(sumDfCredit),
            numberFormat(sumDfLostTreatment),
            numberFormat(sumDfLostCreditAndTreatment),
            numberFormat(sumDfLabFee),
            numberFormat(sumDfNet),
        ])
        sheet.mergeCells(totalDfRow.number, 1, totalDfRow.number, 2)
        totalDfRow.font = { bold: true }
        totalDfRow.alignment = { horizontal: 'right' }
    } else {
        sheet.addRow([])
        const dfHeader = [
            t('REPORT.TABLE.REMUNERATION.#NO'),
            t('REPORT.TABLE.REMUNERATION.DOCTOR'),
            t('REPORT.TABLE.REMUNERATION.NET_PRICE'),
            t('ค่าประกันมือ'),
            t('REPORT.TABLE.REMUNERATION.SUM_PAID'),
            t('DF'),
            t('REPORT.TABLE.REMUNERATION.CREDIT_DF'),
            t('REPORT.TABLE.REMUNERATION.TREATMENT_RIGHT_DF'),
            t('REPORT.TABLE.REMUNERATION.SUM_DF'),
            t('DF LAB'),
            t('Net DF'),
        ]
        sheet.addRow(dfHeader).font = { bold: true }
        const noDataRow = sheet.addRow([t('REPORT.TITLE.NO_DATA')])
        sheet.mergeCells(noDataRow.number, 1, noDataRow.number, 11)
        noDataRow.font = { italic: true, color: { argb: 'FF000000' } }
        noDataRow.alignment = { horizontal: 'center' }
    }

    sheet.addRow([])
    sheet.addRow([
        t('REPORT.TABLE.REMUNERATION.INCOME'),
        t('REPORT.TABLE.REMUNERATION.NET_CLINIC'),
        t('REPORT.TABLE.REMUNERATION.DF_NET'),
        t('REPORT.TABLE.REMUNERATION.SUM_DF'),
        t('REPORT.TABLE.REMUNERATION.DF_LAB'),
        t('REPORT.TABLE.REMUNERATION.CASH'),
        t('REPORT.TABLE.REMUNERATION.TRANSFER'),
        t('REPORT.TABLE.REMUNERATION.DEBIT'),
        t('REPORT.TABLE.REMUNERATION.CREDIT'),
        t('REPORT.TABLE.REMUNERATION.TREATMENT_RIGHT'),
        ...(hasCrm ? [
            t('REPORT.TABLE.REMUNERATION.VOUCHER'),
            t('REPORT.TABLE.REMUNERATION.PROMOTION'),
        ] : []),
        t('REPORT.TABLE.REMUNERATION.ADVANCE'),
    ]).font = { bold: true }

    const rows = Array.isArray(data.rows) ? data.rows : []
    const rowDf = Array.isArray(data.rowDf) ? data.rowDf : []

    const sumAmountPaid = _.sumBy(rows, (salary: any) => {
        const table = Array.isArray(salary.table) ? salary.table : []
        return _.sumBy(table, 'amountPaid')
    })
    const sumDfNet = _.sumBy(rowDf, 'dfNet')
    const sumDfLostCredit = _.sumBy(rowDf, 'dfLostCreditAndTreatment')
    const sumCash = _.sumBy(rows, (salary: any) => {
        const table = Array.isArray(salary.table) ? salary.table : []
        return _.sumBy(table, 'amountCash')
    })
    const sumTransfer = _.sumBy(data.rows, (salary: any) => _.sumBy(salary.table, 'amountTransfer'))
    const sumDebit = _.sumBy(data.rows, (salary: any) => _.sumBy(salary.table, 'amountDebit'))
    const sumCredit = _.sumBy(data.rows, (salary: any) => _.sumBy(salary.table, 'amountCredit'))
    const sumTreatmentRight = _.sumBy(data.rows, (salary: any) => _.sumBy(salary.table, 'amountTreatmentRight'))
    const sumVoucherTotal = _.sumBy(data.rows, (salary: any) => _.sumBy(salary.table, 'amountVoucher'))
    const sumPromotionTotal = _.sumBy(data.rows, (salary: any) => _.sumBy(salary.table, 'amountPromotion'))
    const sumAdvance = _.sumBy(data.rows, (salary: any) => _.sumBy(salary.table, 'amountAdvance'))
    const netClinic = sumAmountPaid - sumDfNet < 0 ? 0 : sumAmountPaid - sumDfNet
    const totaSumlDFLab = data.rowDf?.length ? data.rowDf.map((item: any) => {
        const newSum = (item?.dfLostCreditAndTreatment || 0) - (item?.dfNet || 0)
        if (newSum < 0) {
            return 0
        } else {
            return newSum
        }
    }).reduce((a: any, b: any) => Number(a || 0) + Number(b || 0)) : 0
    const newSumDFLab = totaSumlDFLab < 0 ? 0 : totaSumlDFLab
    const summaryRow = sheet.addRow([
        numberFormat(sumAmountPaid),
        numberFormat(netClinic),
        numberFormat(sumDfNet),
        numberFormat(sumDfLostCredit),
        numberFormat(newSumDFLab),
        numberFormat(sumCash),
        numberFormat(sumTransfer),
        numberFormat(sumDebit),
        numberFormat(sumCredit),
        numberFormat(sumTreatmentRight),
        ...(hasCrm ? [
            numberFormat(sumVoucherTotal),
            numberFormat(sumPromotionTotal),
        ] : []),
        numberFormat(sumAdvance),
    ])
    summaryRow.alignment = { horizontal: 'right' }
    const blueFont = { color: { argb: 'FF007BFF' } }
    const blueEnd = hasCrm ? 13 : 11
    for (let col = 6; col <= blueEnd; col++) {
        summaryRow.getCell(col).font = blueFont
    }

    forceTextOnDateCells(workbook)
    const buffer = await workbook.xlsx.writeBuffer()
    const timestamp = moment().format('YYYYMMDD_HHmmss')
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
    saveAs(blob, `${t('REPORT.REPORT_NAME.REMUNERATION')}_${timestamp}.xlsx`)
}

export default ExportToExcel